import React, { useState } from 'react';
import { Target, CheckCircle, Clock, Flag, Calendar, Users, Trophy, Star, ArrowLeft, ChevronRight, Lock, Bot, Brain, Smartphone } from 'lucide-react'

interface RoadmapProps {
  onLaunchPlatform?: () => void;
  onNavigateToPage?: (page: string) => void;
}

export default function RoadmapPage({ onLaunchPlatform, onNavigateToPage }: RoadmapProps) {
  const [currentView, setCurrentView] = useState<string>('main');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const phases = [
    {
      id: 'phase1',
      title: 'Phase 1: V2 BETA Platform Core Release',
      subtitle: 'Current Phase • Team Size: 5 • Core Trading Tools',
      icon: <Clock className="w-5 h-5 text-[#24c35b]" />,
      status: 'Current Phase',
      progress: 70,
      details: {
        title: 'Phase 1: V2 BETA Platform Core Release',
        period: 'Current Phase',
        description: 'Establish a reliable, performant foundation of core tooling and features essential for traders operating in fast-moving Solana markets.',
        teamSize: 5,
        keyFeatures: [
          {
            title: 'Sniper Bot',
            description: 'Ultra-low-latency token launch sniping with real-time liquidity monitoring.'
          },
          {
            title: 'Copy Trader',
            description: 'Follow and mimic top-performing wallets with customizable mirroring parameters.'
          },
          {
            title: 'Auto Trader',
            description: 'Strategy-driven automation for buying/selling based on pre-set conditions.'
          },
          {
            title: 'Wallet Manager',
            description: 'Multi-wallet support.'
          },
          {
            title: 'Smart Watchlist',
            description: 'Track tokens and wallets with custom filters.'
          },
          {
            title: 'Rug Tracker',
            description: 'Identify rug pulls using on-chain metrics.'
          },
          {
            title: 'Whale Tracker',
            description: 'Monitor large transactions and wallet movements to anticipate market activity.'
          },
          {
            title: 'Advanced Charts',
            description: 'Candlestick, volume overlays, and price action with DEX-specific integrations.'
          },
          {
            title: 'Order Book',
            description: 'Live on-chain order depth for tokens and supported DEXs.'
          },
          {
            title: 'Token Info & Stats',
            description: 'Comprehensive token metadata, recent trades, liquidity trends, holder metrics.'
          },
          {
            title: 'Swaps',
            description: 'Direct in-app token swaps via Jupiter aggregator.'
          }
        ],
        goals: [
          {
            title: 'Bug Fixing',
            description: 'Stabilization of newly added features; addressing user-reported issues and edge cases.',
            impact: 'Improved platform stability and user experience'
          },
          {
            title: 'Infrastructure Improvements',
            description: 'Upgrading backend performance, RPC/gRPC optimization, Solana indexing layers.',
            impact: 'Enhanced performance and scalability'
          },
          {
            title: 'UI Tweaking',
            description: 'Enhancing UX for clarity, responsiveness, and workflow efficiency.',
            impact: 'Streamlined user interface and better usability'
          },
          {
            title: 'Feature Fine-Tuning',
            description: 'Adjusting performance thresholds, improving trackers and bots.',
            impact: 'Optimized trading tools and automation'
          },
          {
            title: 'Marketing',
            description: 'Brand positioning, community growth, early influencer onboarding, and awareness campaigns.',
            impact: 'Increased platform adoption and community growth'
          }
        ]
      }
    },
    {
      id: 'phase2',
      title: 'Phase 2: V2 Official Release',
      subtitle: 'ETA: ~1 month • Team Size: 8 • AI & Mobile Integration',
      icon: <Brain className="w-5 h-5 text-[#24c35b]" />,
      status: 'Planned',
      progress: 0,
      details: {
        title: 'Phase 2: V2 Official Release',
        period: 'ETA: ~1 month',
        description: 'Transition from beta to production with refined features and powerful new tools that elevate both usability and intelligence of the platform.',
        teamSize: 8,
        plannedAdditions: [
          {
            title: 'AI Trading Assistant ("Hey, EKKO…")',
            description: 'Conversational trading interface using natural language queries to execute actions, analyze market trends, surface token opportunities, or adjust bot behavior in real time.',
            impact: 'Revolutionary AI-powered trading experience'
          },
          {
            title: 'Vortex Bot',
            description: 'Advanced multi-event trigger bot that combines Sniper, Copy, and Auto strategies with smart conditional logic (e.g., "Buy if wallet X buys + price up 3% + LP added > X SOL").',
            impact: 'Sophisticated automated trading strategies'
          },
          {
            title: 'Advanced Chart Improvements',
            description: 'Enhanced visualizations and position overlays.',
            impact: 'Professional-grade charting capabilities'
          },
          {
            title: 'Mobile App',
            description: 'Native (or hybrid) mobile experience with full support for watchlists, bot control, push notifications, core trading and wallet access.',
            impact: 'Mobile-first trading accessibility'
          }
        ],
        additionalObjectives: [
          {
            title: 'V2 Beta to Full Release Transition',
            description: 'Smooth transition from V2 Beta to full release.',
            impact: 'Seamless user experience migration'
          },
          {
            title: 'Platform Reliability',
            description: 'Increase platform uptime and fault tolerance.',
            impact: '99.9% uptime guarantee'
          },
          {
            title: 'Partner Integrations',
            description: 'Launch partner integrations (where applicable, stay tuned!).',
            impact: 'Expanded ecosystem connectivity'
          },
          {
            title: 'Community Feedback Loop',
            description: 'Community feedback loop integration for new feature rollout.',
            impact: 'User-driven feature development'
          }
        ]
      }
    },
    {
      id: 'phase3',
      title: 'Phase 3: LOCKED',
      subtitle: 'Planning Stage • Team Size: 12-14 • Advanced Expansion',
      icon: <Lock className="w-5 h-5 text-[#24c35b]" />,
      status: 'Planning Stage',
      progress: 0,
      details: {
        title: 'Phase 3: LOCKED (Planning Stage)',
        period: 'Planning Stage',
        description: 'This phase is currently under internal planning. Details will be released once current milestones are achieved and evaluated.',
        teamSize: '12–14',
        note: 'This phase is currently under internal planning. Details will be released once current milestones are achieved and evaluated.',
        potentialFocusAreas: [
          {
            title: 'Full Ecosystem Expansion',
            description: 'SDK/API access for power users.',
            impact: 'Developer ecosystem and third-party integrations'
          },
          {
            title: 'DAO Tooling and Governance Integration',
            description: 'Decentralized governance and community-driven decision making.',
            impact: 'Community ownership and participation'
          },
          {
            title: 'Cross-Chain Integrations (PRIORITY)',
            description: 'If aligned with community needs and liquidity opportunities.',
            impact: 'Multi-blockchain trading capabilities'
          },
          {
            title: 'Advanced AI Trading Logic',
            description: 'Portfolio optimization models.',
            impact: 'Next-generation trading intelligence'
          },
          {
            title: 'Institutional Dashboards',
            description: 'Advanced analytics for funds/teams.',
            impact: 'Enterprise-grade trading solutions'
          }
        ]
      }
    }
  ];

  const handlePhaseClick = (phaseId: string) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView(phaseId);
      setIsTransitioning(false);
    }, 300);
  };

  const handleBackClick = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('main');
      setIsTransitioning(false);
    }, 300);
  };

  const getCurrentPhase = () => phases.find(p => p.id === currentView);

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Background Hero Image - Fixed to bottom */}
      <div className={`fixed bottom-0 left-0 w-full h-full pointer-events-none z-1 transition-opacity duration-300 ${
        currentView !== 'main' ? 'opacity-20' : 'opacity-100'
      }`}>
        <div className="relative h-full flex items-end justify-start">
          <div className="relative">
            <img 
              src="https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/212.png"
              alt="EKKO Roadmap Strategic Vision"
              className="h-full w-auto object-contain object-bottom"
              style={{ 
                height: '100vh',
                maxWidth: '60vw'
              }}
            />
            {/* Right fade gradient */}
            <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-white to-transparent"></div>
            {/* Left fade gradient */}
            <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent"></div>
          </div>
        </div>
      </div>

      {/* Architectural Lines */}
      <div className="fixed inset-0 pointer-events-none z-5">
        {/* Horizontal lines */}
        <div className="absolute top-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#24c35b] to-transparent"></div>
        <div className="absolute bottom-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        
        {/* Vertical lines */}
        <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-0 right-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        
        {/* Cross decorations */}
        <div className="absolute top-32 left-1/4 w-8 h-8 transform -translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        <div className="absolute top-32 right-1/4 w-8 h-8 transform translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        
        {/* Corner plus decorations */}
        <div className="absolute top-8 left-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
        <div className="absolute top-8 right-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
        <div className="absolute bottom-8 left-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
        <div className="absolute bottom-8 right-8 w-5 h-5 text-white bg-white rounded-full shadow-lg flex items-center justify-center z-10">
          <div className="w-3 h-3">
            <div className="absolute inset-0 bg-gray-900 w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full"></div>
            <div className="absolute inset-0 bg-gray-900 h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full"></div>
          </div>
        </div>
      </div>
                
      {/* Background Text - CENTERED AND STRAIGHT */}
      <div className="fixed inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
        <div className="text-gray-200 font-black select-none text-center space-y-8">
          <div className="text-[12vw] opacity-20 leading-none">ROADMAP</div>
          <div className="text-[10vw] opacity-15 leading-none">FUTURE</div>
          <div className="text-[8vw] opacity-10 leading-none">VISION</div>
        </div>
      </div>
                  
      {/* Main Content - Transitioning Views */}
      <section className="relative z-10 min-h-screen flex items-center py-20">
        <div className="container mx-auto px-4">
          
          {/* Main Overview */}
          {currentView === 'main' && (
            <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              <div className="max-w-2xl mx-auto lg:ml-auto lg:mr-0 lg:max-w-3xl">
                {/* Content */}
                <div className="text-center lg:text-right space-y-8">
                  {/* Title */}
                  <div className="space-y-6">
                    <h1 className="text-6xl lg:text-7xl font-black leading-tight">
                      <span className="bg-gradient-to-r from-[#24c35b] via-[#1eaa4e] to-[#24c35b] bg-clip-text text-transparent">
                        ROADMAP
                      </span>
                    </h1>
                    <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                      Building the Future of
                      <br />
                      <span className="text-[#24c35b]">Solana Trading</span>
                    </h2>
                  </div>
                  
                  {/* Description */}
                  <p className="text-xl text-gray-600 leading-relaxed">
                    Discover our strategic vision and development milestones as we revolutionize DeFi trading on Solana.
                  </p>
                  
                  {/* Clickable Timeline Phases */}
                  <div className="grid grid-cols-1 gap-4 max-w-2xl mx-auto lg:mx-0">
                    {phases.map((phase) => (
                      <button
                        key={phase.id}
                        onClick={() => handlePhaseClick(phase.id)}
                        className="w-full glassmorphism p-4 rounded-xl border border-white/20 flex items-center gap-4 hover:bg-gray-50 transition-all duration-300 group"
                      >
                        <div className="w-10 h-10 bg-[#24c35b]/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                          {phase.icon}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-gray-900">{phase.title}</div>
                          <div className="text-sm text-gray-600">{phase.subtitle}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            phase.status === 'Current Phase' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {phase.progress}%
                          </span>
                          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </button>
                    ))}
                  </div>
                  
                  {/* Action Button */}
                  <div className="max-w-lg mx-auto lg:mx-0">
                    <button 
                      onClick={onLaunchPlatform}
                      className="btn-primary px-8 py-4 text-lg font-bold rounded-xl shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 group w-full"
                    >
                      <Trophy className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      <span>Join the Journey</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Detail Views */}
          {currentView !== 'main' && (
            <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
              {(() => {
                const phase = getCurrentPhase();
                if (!phase) return null;

                return (
                  <div className="max-w-6xl mx-auto">
                    {/* Back Button */}
                    <button
                      onClick={handleBackClick}
                      className="mb-8 flex items-center gap-3 glassmorphism px-6 py-3 rounded-xl border border-white/20 hover:bg-gray-50 transition-colors group"
                    >
                      <ArrowLeft className="w-4 h-4 text-[#24c35b] group-hover:-translate-x-1 transition-transform" />
                      <span className="font-medium text-gray-900">Back to Roadmap</span>
                    </button>

                    {/* Phase Header with Architectural Styling */}
                    <div className="text-center mb-16 relative">
                      {/* Architectural corner decorations */}
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-4">
                        <div className="w-16 h-1 bg-gradient-to-r from-transparent via-[#24c35b] to-transparent"></div>
                      </div>
                      


                      {/* Centered Title Section */}
                      <div className="space-y-4 mb-8">
                        <h1 className="text-5xl lg:text-6xl font-black text-gray-900 leading-tight" style={{ fontWeight: '900', textShadow: '0 0 1px currentColor' }}>
                          <span className="bg-gradient-to-r from-[#24c35b] via-[#1eaa4e] to-[#24c35b] bg-clip-text text-transparent">
                            {phase.details.title.split(':')[0].toUpperCase()}
                          </span>
                        </h1>
                        {phase.details.title.includes(':') && (
                          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                            {phase.details.title.split(':')[1]}
                          </h2>
                        )}
                        <div className="flex items-center justify-center gap-6 text-lg">
                          <span className="px-4 py-2 bg-[#24c35b]/10 text-[#24c35b] rounded-full font-semibold border border-[#24c35b]/20">
                            {phase.details.period}
                          </span>
                          <span className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full font-semibold">
                            Team Size: {phase.details.teamSize}
                          </span>
                        </div>
                      </div>

                      {/* Description with Architectural Frame */}
                      <div className="relative max-w-4xl mx-auto">
                        <div className="relative glassmorphism p-8 rounded-2xl border border-white/20 shadow-xl">
                          {/* Corner architectural elements */}
                          <div className="absolute top-0 left-0 w-6 h-6">
                            <div className="absolute top-0 left-0 w-full h-0.5 bg-[#24c35b]/50"></div>
                            <div className="absolute top-0 left-0 w-0.5 h-full bg-[#24c35b]/50"></div>
                          </div>
                          <div className="absolute top-0 right-0 w-6 h-6">
                            <div className="absolute top-0 right-0 w-full h-0.5 bg-[#24c35b]/50"></div>
                            <div className="absolute top-0 right-0 w-0.5 h-full bg-[#24c35b]/50"></div>
                          </div>
                          <div className="absolute bottom-0 left-0 w-6 h-6">
                            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#24c35b]/50"></div>
                            <div className="absolute bottom-0 left-0 w-0.5 h-full bg-[#24c35b]/50"></div>
                          </div>
                          <div className="absolute bottom-0 right-0 w-6 h-6">
                            <div className="absolute bottom-0 right-0 w-full h-0.5 bg-[#24c35b]/50"></div>
                            <div className="absolute bottom-0 right-0 w-0.5 h-full bg-[#24c35b]/50"></div>
                          </div>
                          
                          <p className="text-xl text-gray-600 leading-relaxed relative z-10">
                            {phase.details.description}
                          </p>
                        </div>
                      </div>

                      {phase.details.note && (
                        <div className="mt-8 relative max-w-4xl mx-auto">
                          <div className="relative bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl p-6 shadow-lg">
                            {/* Warning architectural corners */}
                            <div className="absolute top-0 left-0 w-4 h-4">
                              <div className="absolute top-0 left-0 w-full h-1 bg-amber-400"></div>
                              <div className="absolute top-0 left-0 w-1 h-full bg-amber-400"></div>
                            </div>
                            <div className="absolute top-0 right-0 w-4 h-4">
                              <div className="absolute top-0 right-0 w-full h-1 bg-amber-400"></div>
                              <div className="absolute top-0 right-0 w-1 h-full bg-amber-400"></div>
                            </div>
                            
                            <p className="text-amber-800 font-semibold text-lg relative z-10">{phase.details.note}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Bottom architectural line */}
                      <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-4">
                        <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-[#24c35b] to-transparent"></div>
                      </div>
                    </div>
                          
                    {/* Content Sections */}
                    <div className="space-y-12">
                      
                      {/* Key Features */}
                      {phase.details.keyFeatures && (
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Key Features</h3>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {phase.details.keyFeatures.map((feature, index) => (
                              <div key={index} className="glassmorphism p-6 rounded-xl border border-white/20 hover:shadow-lg transition-all duration-300">
                                <h4 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h4>
                                <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Goals */}
                      {phase.details.goals && (
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Goals</h3>
                          <div className="grid md:grid-cols-2 gap-8">
                            {phase.details.goals.map((goal, index) => (
                              <div key={index} className="glassmorphism p-8 rounded-2xl border border-white/20 hover:shadow-lg transition-all duration-300">
                                <h4 className="text-xl font-bold text-gray-900 mb-4">{goal.title}</h4>
                                <p className="text-gray-600 leading-relaxed mb-4">{goal.description}</p>
                                <div className="bg-[#24c35b]/10 rounded-lg p-4 border border-[#24c35b]/20">
                                  <p className="text-sm font-medium text-[#24c35b]">Impact: {goal.impact}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Planned Additions */}
                      {phase.details.plannedAdditions && (
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Planned Additions</h3>
                          <div className="grid md:grid-cols-2 gap-8">
                            {phase.details.plannedAdditions.map((addition, index) => (
                              <div key={index} className="glassmorphism p-8 rounded-2xl border border-white/20 hover:shadow-lg transition-all duration-300">
                                <h4 className="text-xl font-bold text-gray-900 mb-4">{addition.title}</h4>
                                <p className="text-gray-600 leading-relaxed mb-4">{addition.description}</p>
                                <div className="bg-[#24c35b]/10 rounded-lg p-4 border border-[#24c35b]/20">
                                  <p className="text-sm font-medium text-[#24c35b]">Impact: {addition.impact}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Additional Objectives */}
                      {phase.details.additionalObjectives && (
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Additional Objectives</h3>
                          <div className="grid md:grid-cols-2 gap-8">
                            {phase.details.additionalObjectives.map((objective, index) => (
                              <div key={index} className="glassmorphism p-8 rounded-2xl border border-white/20 hover:shadow-lg transition-all duration-300">
                                <h4 className="text-xl font-bold text-gray-900 mb-4">{objective.title}</h4>
                                <p className="text-gray-600 leading-relaxed mb-4">{objective.description}</p>
                                <div className="bg-[#24c35b]/10 rounded-lg p-4 border border-[#24c35b]/20">
                                  <p className="text-sm font-medium text-[#24c35b]">Impact: {objective.impact}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Potential Focus Areas */}
                      {phase.details.potentialFocusAreas && (
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Potential Focus Areas</h3>
                          <p className="text-center text-gray-600 mb-8 italic">(not speculative, but inferred from current growth pattern)</p>
                          <div className="grid md:grid-cols-2 gap-8">
                            {phase.details.potentialFocusAreas.map((area, index) => (
                              <div key={index} className="glassmorphism p-8 rounded-2xl border border-white/20 hover:shadow-lg transition-all duration-300 opacity-75">
                                <h4 className="text-xl font-bold text-gray-900 mb-4">{area.title}</h4>
                                <p className="text-gray-600 leading-relaxed mb-4">{area.description}</p>
                                <div className="bg-[#24c35b]/10 rounded-lg p-4 border border-[#24c35b]/20">
                                  <p className="text-sm font-medium text-[#24c35b]">Impact: {area.impact}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-16 glassmorphism p-8 rounded-2xl border border-white/20 text-center">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">Progress Status</h3>
                      <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                        <div 
                          className="bg-gradient-to-r from-[#24c35b] to-[#1eaa4e] h-4 rounded-full transition-all duration-1000"
                          style={{ width: `${phase.progress}%` }}
                        ></div>
                      </div>
                      <p className="text-lg font-medium text-gray-600">
                        <span className="text-[#24c35b] font-bold">{phase.progress}%</span> Complete • Status: <span className="text-[#24c35b]">{phase.status}</span>
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </section>

      {/* Development Stats - Bottom */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="glassmorphism px-8 py-4 rounded-xl border border-white/20 flex items-center gap-8">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">3 Major Phases</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">V2 Beta Active</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">5-14 Team Growth</span>
          </div>
        </div>
      </div>
    </div>
  );
} 